import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import {
  GraduationCap, Luggage, Gift, Users, CheckCircle2, ArrowRight, Home,
  Truck, Zap, Clock, IndianRupee, Heart, MessageCircle, Sparkles, ShieldCheck,
} from "lucide-react";

// Fallback used while settings fetch is in-flight — actual number comes from
// /api/site-settings (admin-editable at /dashboard/contact-settings).
const FALLBACK_WA = "917995865243";
const FALLBACK_MSG = "Hi PickNGo! I'm a college student and want to book a pickup.";

// Campus Ambassador application template — DIFFERENT from shipment booking.
// Opens WhatsApp with blanks for the student to fill in. Do not merge this
// into the booking template; the two flows are intentionally separate.
const AMBASSADOR_MSG = `Hello PickNGo Team,
I want to become a Campus Ambassador.

Name:
College:
Phone Number:
Email:
Year/Branch:

Please share the next steps.`;

const STEPS = [
  { icon: MessageCircle, title: "Message us on WhatsApp", desc: "Tell us your hostel, home city and pickup date. We reply within 15 minutes." },
  { icon: Clock, title: "Schedule your pickup", desc: "Pick a 2-hour slot convenient for you. Our agent reaches your hostel on time." },
  { icon: Truck, title: "We pack & ship", desc: "Certified packing, barcoded tags, and transit insurance included on all college routes." },
  { icon: Home, title: "Delivered home", desc: "Flat-rate delivery to 19,000+ pincodes. Daily tracking updates via WhatsApp." },
];

const PRICE_CARDS = [
  { size: "1 Duffel / Suitcase", weight: "Up to 15 kg", price: "₹599", note: "Most students pick this" },
  { size: "2 Pieces Combo", weight: "Up to 30 kg", price: "₹999", note: "Great value — ₹500 saved" },
  { size: "Bulk — 3+ pieces", weight: "Up to 60 kg", price: "From ₹1,499", note: "Custom quote on WhatsApp" },
];

const PERKS = [
  { icon: Gift, title: "Free shipping coupon", desc: "Refer 10 friends → your next shipment is FREE" },
  { icon: IndianRupee, title: "₹500 Ambassador Voucher", desc: "Become a college ambassador and earn every semester" },
  { icon: Heart, title: "₹30 per friend", desc: "Share on WhatsApp — you both earn wallet credit" },
  { icon: ShieldCheck, title: "Transit insurance included", desc: "Up to ₹5,000 auto-cover for damage / loss" },
];

export default function CollegeLanding() {
  // Admin-editable contact info (see /dashboard/contact-settings).
  const [waNumber, setWaNumber] = useState(FALLBACK_WA);
  const [waMsg, setWaMsg] = useState(FALLBACK_MSG);
  // CMS-driven pricing block (Phase COLLEGE-PRICING-1). Falls back to the
  // static PRICE_CARDS array above if the API errors / is slow — the
  // marketing page never collapses.
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/site-settings`;
    axios.get(url)
      .then(({ data }) => {
        if (data?.whatsapp_number) setWaNumber(data.whatsapp_number);
        // Combine admin's default message with a college-specific prompt so
        // the pre-filled chat still guides students through the 4 fields we
        // need (pickup, destination, bag count, date).
        const collegeExtras = "\n• Pickup from: \n• Deliver to home (city): \n• Number of bags: \n• Pickup date: ";
        const base = data?.whatsapp_default_message || FALLBACK_MSG;
        setWaMsg(base + collegeExtras);
      })
      .catch(() => { /* fallback already set */ });
  }, []);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/college-pricing`;
    axios.get(url)
      .then(({ data }) => setPricing(data))
      .catch((err) => {
        // Marketing page — keep PRICE_CARDS fallback, log silently.
        // eslint-disable-next-line no-console
        console.warn("[CollegeLanding] college-pricing fetch failed", err?.message);
      });
  }, []);

  const whatsappLink = `https://wa.me/${waNumber.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waMsg)}`;

  // Campus Ambassador opens a DIFFERENT prefilled message on the same number.
  // Built from AMBASSADOR_MSG only — never from waMsg — so the shipment
  // booking template (pickup / drop / bags / date) never leaks into this flow.
  const ambassadorLink = `https://wa.me/${waNumber.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(AMBASSADOR_MSG)}`;

  // Defensive onClick — guarantees a new tab even when the page is embedded
  // in an iframe sandbox (the Emergent preview does this), where the default
  // `<a target="_blank">` behaviour can be silently blocked. We call both
  // `stopPropagation` and `window.open` so the native anchor navigation is
  // replaced with an explicit popup — the browser reliably routes wa.me to
  // the installed app / WhatsApp Web / install page as fallback.
  const openWhatsApp = (e) => {
    if (e) e.stopPropagation();
    try {
      const w = window.open(whatsappLink, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocker engaged — fall back to same-tab navigation.
        window.location.href = whatsappLink;
      }
    } catch {
      window.location.href = whatsappLink;
    }
  };

  // Same popup/fallback strategy as openWhatsApp but routes to the
  // Campus Ambassador template. Kept separate so the booking flow can
  // never accidentally pick up this handler (and vice versa).
  const openAmbassadorWhatsApp = (e) => {
    if (e) e.stopPropagation();
    try {
      const w = window.open(ambassadorLink, "_blank", "noopener,noreferrer");
      if (!w) {
        window.location.href = ambassadorLink;
      }
    } catch {
      window.location.href = ambassadorLink;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo size="sm" /></Link>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp}>
            <Button className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 sm:px-5 text-xs sm:text-sm" data-testid="nav-whatsapp-btn">
              <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp Us
            </Button>
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1E293B] via-[#0F172A] to-[#1E293B] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,#FF6B00_0%,transparent_40%)] opacity-20" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-28 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 bg-[#FF6B00]/15 border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-bold px-3 py-1 rounded-full"><GraduationCap className="h-3 w-3" /> COLLEGE EXPRESS</span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 tracking-tight leading-[1.05]">
              Hostel to <span className="text-[#FF6B00]">Home</span>.<br />Handled.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 mt-5 leading-relaxed max-w-md">
              Semester over? Book your luggage pickup on WhatsApp in 30 seconds. We handle packing, transit and delivery straight to your home city.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp}>
                <Button size="lg" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-7 h-12 text-sm font-semibold" data-testid="hero-whatsapp-btn">
                  <MessageCircle className="mr-2 h-4 w-4" /> Book on WhatsApp
                </Button>
              </a>
              <a href="#pricing">
                <Button size="lg" variant="outline" className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 px-7 h-12 text-sm font-semibold" data-testid="hero-pricing-btn">
                  See Pricing
                </Button>
              </a>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
              {[
                { n: "120+", l: "Colleges served" },
                { n: "19K+", l: "Pincodes" },
                { n: "24-48h", l: "Pickup slot" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-['Outfit'] text-2xl font-bold text-[#FF6B00]">{s.n}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden md:block">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF6B00]/40 via-transparent to-transparent blur-3xl" />
            <div className="relative rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
              <Luggage className="h-12 w-12 text-[#FF6B00] mb-4" />
              <p className="font-['Outfit'] font-bold text-xl leading-tight">End-semester?<br />Booking open.</p>
              <div className="mt-5 space-y-3">
                {["✓ Hostel pickup", "✓ Professional packing", "✓ Safe transit", "✓ Home delivery"].map((l) => (
                  <div key={l} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {l.replace("✓ ", "")}</div>
                ))}
              </div>
              <div className="mt-6 p-3 rounded-xl bg-emerald-500/20 border border-emerald-400/30">
                <p className="text-xs font-bold text-emerald-200 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Mega Pickup Day</p>
                <p className="text-[11px] text-emerald-100/80 mt-0.5">Every April 30 &amp; December 15 on 50+ campuses.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Booking in 4 quick steps</h2>
        <p className="text-slate-500 mb-10 max-w-md">WhatsApp-first. No app downloads. No paperwork.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative p-5 rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid={`step-${i + 1}`}>
              <div className="absolute -top-3 -left-3 h-8 w-8 rounded-xl bg-[#FF6B00] text-white text-sm font-bold flex items-center justify-center shadow">{i + 1}</div>
              <s.icon className="h-8 w-8 text-[#FF6B00] mb-3" />
              <h3 className="font-['Outfit'] font-bold text-slate-900 mb-1">{s.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing (CMS-driven; Phase COLLEGE-LANDING-RESTORE) */}
      <section id="pricing" className="bg-white border-y border-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
                data-testid="college-landing-pricing-heading">
              {pricing?.page?.heading || "Transparent college-only pricing"}
            </h2>
            <p className="text-slate-500"
                data-testid="college-landing-pricing-subheading">
              {pricing?.page?.subheading || "Flat rates based on luggage size. No surprises. GST will be added."}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5"
                data-testid="college-landing-pricing-cards"
                data-source={pricing ? "cms" : "fallback"}>
            {pricing
              ? (pricing.cards || [])
                  .filter((c) => c.active !== false)
                  .slice()
                  .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                  .map((c) => {
                    const showBadge = !!pricing.page?.most_popular_enabled && !!c.is_most_popular;
                    // Format price = prefix + value + suffix verbatim (the CMS
                    // already encodes "₹" / "From ₹" in `price_prefix`).
                    const priceText = `${c.price_prefix || ""}${c.price_value || ""}${c.price_suffix || ""}`;
                    const waHref = `https://wa.me/${(c.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(c.whatsapp_prefilled_message || "")}`;
                    return (
                      <div key={c.card_id}
                            data-testid={`price-card-${c.card_id}`}
                            data-card-title={c.title}
                            data-is-most-popular={showBadge ? "true" : "false"}
                            className={`rounded-3xl p-6 border-2 relative ${
                              showBadge
                                ? "border-[#FF6B00] bg-orange-50/40 shadow-lg shadow-[#FF6B00]/10"
                                : "border-slate-100 bg-white"
                            }`}>
                        {showBadge && (
                          <span className="absolute -top-3 left-6 text-[10px] font-bold bg-[#FF6B00] text-white px-3 py-1 rounded-full tracking-wider"
                                  data-testid={`price-card-${c.card_id}-badge`}>
                            {pricing.page?.most_popular_badge_text || "MOST POPULAR"}
                          </span>
                        )}
                        <Luggage className="h-8 w-8 text-[#FF6B00] mb-3" />
                        <h3 className="font-['Outfit'] font-bold text-slate-900"
                            data-testid={`price-card-${c.card_id}-title`}>
                          {c.title}
                        </h3>
                        {c.subtitle && (
                          <p className="text-xs text-slate-500 mt-0.5">{c.subtitle}</p>
                        )}
                        <p className="font-['Outfit'] text-3xl font-extrabold text-slate-900 mt-4"
                            data-testid={`price-card-${c.card_id}-price`}>
                          {priceText}
                        </p>
                        {c.footer_tagline && (
                          <p className="text-xs text-emerald-600 font-semibold mt-1">
                            {c.footer_tagline}
                          </p>
                        )}
                        <a href={waHref}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`price-card-${c.card_id}-cta`}
                            className="block mt-5">
                          <Button className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white h-10 text-sm">
                            {c.whatsapp_button_text || "Book on WhatsApp"}
                          </Button>
                        </a>
                      </div>
                    );
                  })
              : PRICE_CARDS.map((p, i) => (
                  <div key={p.size}
                        className={`rounded-3xl p-6 border-2 ${i === 1 ? "border-[#FF6B00] bg-orange-50/40 shadow-lg shadow-[#FF6B00]/10" : "border-slate-100 bg-white"} relative`}
                        data-testid={`price-card-${i}`}>
                    {i === 1 && <span className="absolute -top-3 left-6 text-[10px] font-bold bg-[#FF6B00] text-white px-3 py-1 rounded-full tracking-wider">MOST POPULAR</span>}
                    <Luggage className="h-8 w-8 text-[#FF6B00] mb-3" />
                    <h3 className="font-['Outfit'] font-bold text-slate-900">{p.size}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{p.weight}</p>
                    <p className="font-['Outfit'] text-3xl font-extrabold text-slate-900 mt-4">{p.price}</p>
                    <p className="text-xs text-emerald-600 font-semibold mt-1">{p.note}</p>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp} className="block mt-5">
                      <Button className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white h-10 text-sm" data-testid={`book-${i}`}>Book on WhatsApp</Button>
                    </a>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Student perks built in</h2>
        <p className="text-slate-500 mb-10">Refer friends, become an ambassador, save on every pickup.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PERKS.map((p) => (
            <div key={p.title} className="p-5 rounded-2xl bg-white border border-slate-100" data-testid={`perk-${p.title.split(" ")[0].toLowerCase()}`}>
              <p.icon className="h-7 w-7 text-[#FF6B00] mb-3" />
              <h3 className="font-['Outfit'] font-bold text-slate-900 text-sm">{p.title}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ambassador CTA */}
      <section className="py-16 bg-gradient-to-br from-[#FF6B00] via-[#FF9A3C] to-[#FFB56B]">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-95" />
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold mb-3">Become a Campus Ambassador</h2>
          <p className="text-white/90 mb-7 max-w-lg mx-auto">Earn ₹500 per semester + free shipping for 10 referrals. Run Mega Pickup Day on your campus with us.</p>
          <a href={ambassadorLink} target="_blank" rel="noopener noreferrer" onClick={openAmbassadorWhatsApp}>
            <Button size="lg" className="rounded-full bg-white text-[#FF6B00] hover:bg-white/90 px-8 h-12 text-sm font-bold" data-testid="ambassador-cta">
              <MessageCircle className="mr-2 h-4 w-4" /> Apply on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Floating WhatsApp */}
      <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp} className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-3 shadow-lg shadow-emerald-500/30 transition" data-testid="floating-whatsapp">
        <MessageCircle className="h-5 w-5" />
        <span className="font-semibold text-sm hidden sm:inline">Book on WhatsApp</span>
      </a>
    </div>
  );
}
